import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Palette, ImagePlus, Loader2, Check, RotateCcw, Sun, Moon } from "lucide-react";
import { BrandFont, loadFontStylesheet } from "@/components/BrandFont";
import { SettingsSection } from "@/components/SettingsSection";

const PRESETS = [
  ["#D32F2F", "Redline red"], ["#1B7F4B", "Forest green"], ["#1D5FBF", "Lake blue"],
  ["#0D9488", "Teal"], ["#7C3AED", "Violet"], ["#B45309", "Amber"], ["#BE185D", "Berry"], ["#111827", "Charcoal"],
];
const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

export const companyLogoUrl = (companyId, ver) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public/company/${companyId}/logo?v=${ver || ""}`;

// The portfolio's own logo, colour and font — what every manager in the firm sees between communities.
// Every change saves itself (no Save button to forget): presets/font/logo right away, the custom
// colour picker once you stop dragging.
export function CompanyBrandCard({ companyId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [name, setName] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle · saving · saved · error
  const logoRef = useRef(null);
  const pending = useRef(null);   // form waiting to be saved (null = nothing pending)
  const timer = useRef(null);

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/brand`)
      .then((r) => { setData(r.data); setName(r.data.name || ""); setForm({ color: r.data.color || "", font: r.data.font || "default", logo_path: r.data.logo_path || "", sidebar_theme: r.data.sidebar_theme === "dark" ? "dark" : "light" }); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the portfolio brand"));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (data?.font_options || []).forEach((f) => loadFontStylesheet(f.google)); }, [data?.font_options]);

  const save = useCallback(async () => {
    const body = pending.current;
    if (!body) return;
    pending.current = null;
    setStatus("saving");
    try {
      const r = await api.put(`/companies/${companyId}/brand`, body);
      setData(r.data);
      window.dispatchEvent(new CustomEvent("askredline:company-brand-updated"));
      if (!pending.current) { setStatus("saved"); setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 2500); }
    } catch (e) {
      pending.current = body;
      setStatus("error");
      toast.error(e?.response?.data?.detail || "Couldn't save the portfolio brand");
    }
  }, [companyId]);
  const upd = (k, v, delay = 0) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      pending.current = next;
      return next;
    });
    setStatus("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, delay);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const saveName = async () => {
    const n = name.trim();
    if (!n || n === data.name) { setName(data.name); return; }
    setNameBusy(true);
    try {
      await api.put(`/companies/${companyId}`, { name: n });
      setData((d) => ({ ...d, name: n }));
      toast.success("Portfolio renamed");
      window.dispatchEvent(new CustomEvent("askredline:company-brand-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't rename"); setName(data.name); }
    finally { setNameBusy(false); }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Keep the logo under 4 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); upd("logo_path", r.data.path); toast.success("Logo saved"); }
    catch { toast.error("Upload failed"); }
  };

  if (!data || !form) {
    return <div className="rounded-2xl border border-sand-200 bg-white p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="company-brand-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading brand…</div>;
  }
  const readOnly = !data.can_edit;
  const color = form.color || "#D32F2F";
  const font = (data.font_options || []).find((f) => f.key === (form.font || "default"));
  const logoLive = form.logo_path && form.logo_path === data.logo_path;

  return (
    <SettingsSection id="company-brand-card" icon={Palette} title="Portfolio brand" defaultOpen
      badges={[status === "error" && { text: "not saved", warn: true }]}
      summary={`${form.logo_path ? "Logo set" : "No logo yet"} · colour ${color} · ${font?.label || "default font"} — worn by the portfolio console for everyone at ${data.name}. Changes save automatically.`}
      headerRight={!readOnly ? (
        status === "error" ? (
          <button data-testid="company-brand-retry" onClick={() => { pending.current = pending.current || form; save(); }}
            className="shrink-0 rounded-full font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1.5 bg-amber-500 text-white hover:bg-amber-600 transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Couldn't save · retry
          </button>
        ) : (
          <span data-testid="company-brand-autosave" aria-live="polite"
            className={`shrink-0 text-xs font-semibold flex items-center gap-1.5 transition-opacity duration-500 ${status === "idle" ? "opacity-0" : "opacity-100"} ${status === "saved" ? "text-emerald-700" : "text-muted-foreground"}`}>
            {status === "saving" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : <><Check className="h-3.5 w-3.5" /> Saved</>}
          </span>
        )
      ) : null}>
      <p className="text-sm text-muted-foreground -mt-1">
        This is <span className="font-semibold text-ink">{data.name}</span>'s look between communities — the sidebar mark, the selected-menu colour and the console font every manager in the firm sees.
        Each community keeps its own brand for residents{data.communities ? ` (${data.communities} in this portfolio)` : ""}.
      </p>

      <div className="w-full md:w-96">
        <label className="text-sm font-semibold" htmlFor="company-brand-name">Portfolio name</label>
        <div className="mt-1 flex items-center gap-2">
          <input id="company-brand-name" data-testid="company-brand-name" disabled={readOnly || nameBusy} value={name} maxLength={80}
            onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className={inputCls} placeholder="Your management company" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">Shown in the sidebar, the community switcher and every portfolio page. Saves when you click away — everything below saves the moment you change it.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {PRESETS.map(([hex, label]) => {
          const active = color.toLowerCase() === hex.toLowerCase();
          return (
            <button key={hex} data-testid={`company-brand-color-${hex.slice(1)}`} disabled={readOnly} title={label}
              onClick={() => upd("color", hex === "#D32F2F" ? "" : hex)}
              className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${active ? "border-ink scale-110" : "border-transparent"}`}
              style={{ backgroundColor: hex }} />
          );
        })}
        <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          Custom
          <input type="color" data-testid="company-brand-color-custom" disabled={readOnly} value={color}
            onChange={(e) => upd("color", e.target.value, 600)} className="h-5 w-7 cursor-pointer border-0 bg-transparent p-0" />
        </label>
        {form.color && !readOnly && (
          <button data-testid="company-brand-color-reset" onClick={() => upd("color", "")} className="text-xs font-bold text-muted-foreground hover:text-redline-600">Reset to Redline red</button>
        )}
        <span className="rounded-full text-white text-xs font-bold px-4 py-1.5" style={{ backgroundColor: color }} data-testid="company-brand-color-preview">Selected menu item</span>
      </div>

      <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-sand-200">
        <div className="min-w-0">
          <label className="text-sm font-semibold">Community sidebar</label>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">How the sidebar looks <span className="font-semibold text-ink">inside a community</span> for everyone in your firm. Your Portfolio and Redline views stay dark so you always know which level you're on. A community can override this on its own profile.</p>
        </div>
        <div className="flex gap-0.5 rounded-full border border-sand-200 bg-white p-0.5 shrink-0" data-testid="company-brand-sidebar-theme">
          {[["light", "Light", Sun], ["dark", "Dark", Moon]].map(([val, lbl, Icon]) => {
            const active = (form.sidebar_theme || "light") === val;
            return (
              <button key={val} data-testid={`company-brand-sidebar-theme-${val}`} disabled={readOnly}
                onClick={() => upd("sidebar_theme", val)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                <Icon className="h-3.5 w-3.5" /> {lbl}
              </button>
            );
          })}
        </div>
        <span className={`rounded-xl px-3 py-2 text-xs font-bold shrink-0 ${(form.sidebar_theme || "light") === "dark" ? "bg-ink text-white" : "bg-white text-ink border border-sand-200"}`}
          data-testid="company-brand-sidebar-theme-preview">
          <span style={{ color: color }}>●</span> Community sidebar
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full md:w-80">
          <label className="text-sm font-semibold">Console font</label>
          <select data-testid="company-brand-font" disabled={readOnly} value={form.font || "default"} onChange={(e) => upd("font", e.target.value)} className={`${inputCls} mt-1`}>
            {(data.font_options || []).map((f) => <option key={f.key} value={f.key}>{f.label} — {f.blurb}</option>)}
          </select>
        </div>
        {font && (
          <div className="rounded-xl border border-sand-200 bg-sand-50 px-4 py-3 min-w-0 flex-1" data-testid="company-brand-font-preview">
            <BrandFont brand={{ font_css: font.google, font_heading: font.heading, font_body: font.body }} scope="[data-companyfont-preview]" />
            <div data-companyfont-preview>
              <div className="font-heading font-bold text-ink text-lg" style={{ fontFamily: font.heading }}>{data.name}</div>
              <div className="text-sm text-ink/70" style={{ fontFamily: font.body }}>Every community answered, in one portfolio view.</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-sand-200">
        {form.logo_path ? (
          <>
            {logoLive && <img src={companyLogoUrl(companyId, data.console?.logo_ver)} alt="" data-testid="company-brand-logo-thumb" className="h-10 max-w-[7rem] object-contain rounded-lg border border-sand-200 bg-white p-1" />}
            <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5 font-bold" data-testid="company-brand-logo-set">Logo set ✓</span>
          </>
        ) : <span className="text-xs text-muted-foreground" data-testid="company-brand-logo-none">No logo yet — the sidebar shows the first letter of {data.name} in your colour.</span>}
        {!readOnly && (
          <>
            <button data-testid="company-brand-upload-logo" onClick={() => logoRef.current?.click()}
              className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5">
              <ImagePlus className="h-4 w-4" /> {form.logo_path ? "Replace logo" : "Upload logo"}
            </button>
            {form.logo_path && <button data-testid="company-brand-remove-logo" onClick={() => upd("logo_path", "")} className="text-xs font-bold text-muted-foreground hover:text-danger-600">Remove</button>}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
          </>
        )}
      </div>
    </SettingsSection>
  );
}
